import { defineContract } from "../defineContract";
import { SOCKET_EVENTS } from "../socketEvents";

/**
 * Shape mínimo que as telas usam hoje (lista de contatos, seleção em modal).
 * O model Contact no backend tem bem mais colunas (carteira, documentos,
 * extraInfo, tags...) — não duplicamos aqui até um consumidor precisar, para
 * este arquivo não virar espelho do model e apodrecer sozinho.
 */
export interface ContactDTO {
  id: number;
  name: string;
  number: string;
  email: string;
  profilePicUrl?: string;
  isGroup: boolean;
  companyId: number;
}

/**
 * Payload de SOCKET_EVENTS.CONTACT.CHANGED — o que ContactController,
 * CreateOrUpdateContactService e o import de contatos emitem hoje.
 *
 * As ações em massa não mandam o contato: "delete-all" limpa a lista inteira
 * e "reload" é um pedido para a tela buscar de novo (usado quando o efeito no
 * servidor é grande demais para descrever num evento).
 */
export type ContactEventPayload =
  | { action: "create"; contact: ContactDTO }
  | { action: "update"; contact: ContactDTO }
  | { action: "delete"; contactId: number | string }
  | { action: "bulk-delete"; contactIds: Array<number | string> }
  | { action: "delete-all" }
  | { action: "reload" };

export const contactsContract = defineContract<
  { searchParam?: string; pageNumber?: string | number },
  { contacts: ContactDTO[]; count: number; hasMore: boolean },
  ContactEventPayload
>({
  route: "/contacts",
  method: "GET",
  events: { changed: SOCKET_EVENTS.CONTACT.CHANGED },
  // Muda mais que todos — e é por isso que o TTL pode ser longo: quando muda
  // de verdade, o evento dispara. O TTL só evita rede quando NADA aconteceu.
  cache: { ttl: 5 * 60 * 1000, invalidatedBy: ["changed"] }
});

/** Quantos registros mudam de dono na mesclagem — GET /contacts/:id/merge-preview. */
export interface ContactMergeCounts {
  messages: number;
  tickets: number;
  /** tickets open/pending do contato absorvido — continuam abertos. */
  openTickets: number;
  schedules: number;
  contactTags: number;
  contactDocuments: number;
  contactCustomFields: number;
  contactWallets: number;
  dialogChatBots: number;
  ticketNotes: number;
  campaignShippings: number;
}

export interface ContactMergeSide {
  id: number;
  name: string;
  number: string;
  isGroup: boolean;
  urlPicture?: string | null;
}

/** Resposta de GET /contacts/:contactId/merge-preview?sourceId=. */
export interface ContactMergePreview {
  target: ContactMergeSide;
  source: ContactMergeSide;
  counts: ContactMergeCounts;
}

/** Payload de SOCKET_EVENTS.CONTACT.MERGE. */
export type ContactMergeEventPayload =
  /** Em andamento: `moved` de `total` mensagens já passaram (1 evento por lote). */
  | { status: "running"; targetId: number; sourceId: number; requestedBy: number; moved: number; total: number }
  | { status: "done"; targetId: number; sourceId: number; requestedBy: number; result: Record<string, number> }
  | { status: "failed"; targetId: number; sourceId: number; requestedBy: number; error: string };
